import { RetryLink } from '@apollo/client/link/retry';
import { getMainDefinition } from '@apollo/client/utilities';
import type { Operation } from '@apollo/client';

/**
 * Retries GraphQL queries that data-nexus rejected with 429 Too Many Requests.
 *
 * After a deploy every open tab refetches at once and some requests are rejected. The
 * rejection says when to come back (Retry-After), so wait that long instead of showing an
 * error. Bounded: a few tries, a ceiling on the wait, and jitter so tabs don't all return
 * at the same instant and get rejected again.
 */
export const RATE_LIMIT_MAX_RETRIES = 3;
export const RATE_LIMIT_MAX_DELAY_MS = 30 * 1000;
const RATE_LIMIT_BASE_DELAY_MS = 1000;
const RATE_LIMIT_MAX_JITTER_MS = 500;

type RateLimitError = { statusCode?: number; response?: { headers?: { get(name: string): string | null } } };

const isRateLimited = (error: unknown) => (error as RateLimitError | undefined)?.statusCode === 429;

/** Retry-After is either a number of seconds or an HTTP date. Returns milliseconds, or undefined. */
export const parseRetryAfter = (value: string | null | undefined, now = Date.now()) => {
  const text = value?.trim();
  if (!text) {
    return undefined;
  }
  if (/^\d+(\.\d+)?$/.test(text)) {
    return Number(text) * 1000;
  }
  const date = Date.parse(text);
  return Number.isNaN(date) ? undefined : Math.max(0, date - now);
};

const isMutation = (operation: Operation) => {
  const definition = getMainDefinition(operation.query);
  return definition.kind === 'OperationDefinition' && definition.operation === 'mutation';
};

export const rateLimitDelay = (count: number, error: unknown, random = Math.random) => {
  const retryAfter = parseRetryAfter((error as RateLimitError)?.response?.headers?.get('retry-after'));
  // Without a hint: 1 s, 2 s, 4 s.
  const wait = retryAfter ?? RATE_LIMIT_BASE_DELAY_MS * 2 ** (count - 1);
  return Math.min(wait, RATE_LIMIT_MAX_DELAY_MS) + Math.floor(random() * RATE_LIMIT_MAX_JITTER_MS);
};

export const createRateLimitRetryLink = () =>
  new RetryLink({
    delay: (count, _operation, error) => rateLimitDelay(count, error),
    attempts: (count, operation, error) =>
      // A mutation may have been applied before the response was lost; never repeat it blindly.
      // Only 429 is retried: any other failure is reported as before.
      count <= RATE_LIMIT_MAX_RETRIES && isRateLimited(error) && !isMutation(operation)
  });
